import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import ReactDOMServer from "react-dom/server";
import { IconHandClick, IconArrowNarrowRight } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import estasseguro from "../../assets/bg/estasseguro-sc2-quiz.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar-pre.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar-pre.png";
import imageHint from "../../assets/hints/imageHintOrange.png";
import imageHintUsed from "../../assets/btnPistaOrange.png";
import unIntento from "../../assets/un-intento-orange.png";
import dosIntentos from "../../assets/dos-intentos-orange.png";
import "../StartGame.css";
import "./Scenario2.css";

const SC2_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--orange";
const SC2_HINT_THEME_CLASS = `${SC2_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();

const SWAL_ICON_ARROW_WHITE = ReactDOMServer.renderToStaticMarkup(
	<IconArrowNarrowRight size={50} color="white" stroke={2} />
);

const SC2_HINT_MARK_HTML = hintMarkPistaHtml();

const QUESTION_STEM =
	"Una vez confirmado el adenocarcinoma de colon mediante biopsia, ¿cuál es el siguiente paso más adecuado antes de decidir el tratamiento?";

const RETROALIMENTACION =
	"Con diagnóstico histológico de adenocarcinoma colorrectal invasivo, el siguiente paso orientado a la decisión terapéutica es completar la estadificación " +
	"(extensión locorregional y a distancia), habitualmente mediante TC toraco-abdomino-pélvico y valoración de marcadores como el CEA basal. " +
	"No se debe programar cirugía ni quimioterapia de forma empírica sin esta información; la PET-TC no es obligatoria de rutina en todos los casos.";

const SCENARIO2_QUIZ3_BASE_OPTIONS = [
	{
		id: 1,
		label: "A",
		text: "Programar cirugía directamente sin más estudios.",
		correct: false,
		img: null,
		explanation: "",
	},
	{
		id: 2,
		label: "B",
		text: "Iniciar quimioterapia sistémica de forma inmediata.",
		correct: false,
		img: null,
		explanation: "",
	},
	{
		id: 3,
		label: "C",
		text: "Completar la estadificación con pruebas de imagen y marcador basal, incluyendo TC toraco-abdomino-pélvico y CEA.",
		correct: true,
		img: null,
		explanation: "",
	},
	{
		id: 4,
		label: "D",
		text: "Solicitar PET-TC como prueba obligatoria en todos los pacientes.",
		correct: false,
		img: null,
		explanation: "",
	},
];

function Scenario2Quiz3() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.QUIZ3;
	const SCENARIO_ID = SCENARIOS.SCENARIO_2;

	const [selectedOption, setSelectedOption] = useState(null);
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [attempt, setAttempt] = useState(1);
	const [blockedOptions, setBlockedOptions] = useState(new Set());
	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const NUMBERATTEMPTS = 2;
	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -5;
	const CORRECT_ANSWER_POINTS_2 = 3;
	const WRONG_ANSWER_POINTS_2 = 0;

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "orange");

	const displayOptions = useMemo(() => shuffleQuizOptions(SCENARIO2_QUIZ3_BASE_OPTIONS), []);

	async function submitAnswer(finalScore, correct) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: finalScore,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO2_FIN,
				correct,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch({ type: "GROUP_SET", payload: serverGroup });
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO2_FIN);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC2_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO2_FIN);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		const hint =
			'<div class="txtTexto1 mb-2">Antes de cirugía o quimioterapia curativa o paliativa, suele ser necesario caracterizar la extensión de la enfermedad y el estado basal del paciente con pruebas de imagen estructural y analítica orientada.</div>';

		if (hintUsed) {
			await modalHintShow({
				hint,
				hintMarkHtml: SC2_HINT_MARK_HTML,
				color: "orange",
				big: false,
				themePopupClass: SC2_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			image: imageHint,
			imageHintUsed,
			hint,
			hintMarkHtml: SC2_HINT_MARK_HTML,
			color: "orange",
			big: false,
			themePopupClass: SC2_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(3);
		}
	};

	function checkAnswer() {
		if (!selectedOption) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${estasseguro}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 orange">Deben seleccionar una opción</div>`,
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			});
			return;
		}

		const option = displayOptions.find((o) => o.id === selectedOption);
		if (!option) return;

		if (blockedOptions.has(option.id)) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="txt24 txt-red bold">Esa opción ya la intentaste y era incorrecta. Elijan otra.</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			});
			return;
		}

		modalConfirm({ estasseguro, color: "orange", themePopupClass: SC2_SWAL_POPUP_CLASS }).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			if (option.correct) {
				const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
				const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
				const attemptDelta = base - hintThisAttempt;
				let newScore = pendingScore + attemptDelta;

				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: true,
					question: `${option.label}. ${option.text}`,
					textHtml: `${buildQuizResultAvatarHtml(respuestacorrecta, "correct", "orange")}
                      <div class="txt28 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                      ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                      <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                      ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "orange",
					themePopupClass: SC2_SWAL_POPUP_CLASS,
				}).then(() => {
					submitAnswer(newScore, true);
				});

				return;
			}

			if (attempt < NUMBERATTEMPTS) {
				const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
				const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
				let newScore = pendingScore + attemptDelta;
				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: false,
					question: `${option.label}. ${option.text}`,
					textHtml: `${buildQuizResultAvatarHtml(respuestaincorrecta, "incorrect", "orange")}
                      <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                      <div class="txt22 mt-2">Les queda <b>1</b> intento. Elijan otra opción.</div>
                      <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>
                      <div class="txt18 mt-2 sc2-swal-hint-actions">Pulsa la flecha para intentar con otra opción.</div>`,
					customConfirmButtonHtml: SWAL_ICON_ARROW_WHITE,
					confirmIsIconButton: true,
					color: "orange",
					themePopupClass: SC2_SWAL_POPUP_CLASS,
				}).then(() => {
					setPendingScore(newScore);
					setBlockedOptions((prev) => new Set(prev).add(option.id));
					setAttempt(attempt + 1);
					setSelectedOption(null);
					setIsSubmitting(false);
				});

				return;
			}

			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
			const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt;
			let newScore = pendingScore + attemptDelta;
			if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

			modalQuestionResolution({
				correct: false,
				question: `${option.label}. ${option.text}`,
				textHtml: `${buildQuizResultAvatarHtml(respuestaincorrecta, "incorrect", "orange")}
                    <div class="txt28 bold pt-4">Han agotado los intentos</div>
                    ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                    <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                    ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "orange",
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			}).then(() => {
				submitAnswer(newScore, false);
			});
		});
	}

	const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

	return (
		<div className="start-game-page sc2-context-page sc2-quiz-page">
			<div className="start-game-inner start-game-inner--prep5">
				<span className="start-game-badge sg5-badge">
					<span className="start-game-badge-dot" aria-hidden />
					Escenario 2 · Diagnóstico
				</span>

				<div className="sg5-scroll">
					<header className="sg5-hero">
						<h1 className="sg5-title">Pregunta</h1>
						<p className="sg5-lead sc2-quiz-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick size={28} color="#94a3b8" stroke={1.25} className="sc2-quiz-hand me-2" />
							<span>Seleccionen una opción y pulsen Comprobar respuesta.</span>
						</p>
						<img
							src={remainingAttempts === 2 ? dosIntentos : unIntento}
							alt=""
							className="sc2-quiz-intentos"
						/>
					</div>

					<div className="sc2-quiz-options">
						{displayOptions.map((option) => {
							const isSelected = selectedOption === option.id;
							const isBlocked = blockedOptions.has(option.id);
							return (
								<button
									key={option.id}
									type="button"
									className={`sg5-aside-card sc2-quiz-option text-start ${isSelected ? "sc2-quiz-option--selected" : ""} ${isBlocked ? "sc2-quiz-option--blocked" : ""}`}
									onClick={() => !isBlocked && setSelectedOption(option.id)}
									disabled={isBlocked}
									aria-pressed={isSelected ? "true" : "false"}
									aria-label={`Opción ${option.label}`}
								>
									<div className="sc2-quiz-option-row">
										<span className="sc2-quiz-option-letter">{option.label}</span>
										<span className="sc2-quiz-option-text">{option.text}</span>
									</div>
									{isBlocked ? <div className="sc2-quiz-option-blocked-note">Bloqueada</div> : null}
								</button>
							);
						})}
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? <span className="badge-penalty sc2-quiz-hint-badge">-3</span> : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default Scenario2Quiz3;
